// components/pages/Users/UserManageModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useEnglishText } from "../../../text/useEnglishText.js";
import { manageUser } from "../../../services/userService";
import { useSnackbar } from "../../../components/common/AppSnackbar";


const initialFormData = {
  username: "",
  password: "",
  user_email: "",
  access_child_agents: false,
  is_active: true,
};

const toBoolean = (value, defaultValue = false) => {
  if (value === null || value === undefined || value === "") {
    return defaultValue;
  }

  if (typeof value === "boolean") {
    return value;
  }

  const normalizedValue = String(value).trim().toLowerCase();

  if (["1", "true", "yes", "y", "on"].includes(normalizedValue)) {
    return true;
  }

  if (["0", "false", "no", "n", "off"].includes(normalizedValue)) {
    return false;
  }

  return defaultValue;
};

const normalizeManageUserResponse = (response) => {
  const rawResponse = response?.data ?? response;
  const result = Array.isArray(rawResponse) ? rawResponse[0] : rawResponse;

  if (!result || typeof result !== "object") {
    return {
      success: false,
      message: "",
      data: result,
    };
  }

  const errorNumber =
    result.ERROR_NUMBER ?? result.error_number ?? result.ErrorNumber;

  const isSuccess =
    result.IsSuccess ??
    result.IS_SUCCESS ??
    result.isSuccess ??
    result.is_success;

  const success =
    errorNumber !== undefined && errorNumber !== null && errorNumber !== ""
      ? String(errorNumber).trim() === "0"
      : toBoolean(isSuccess, false);

  const message =
    result.ERROR_MESSAGE ??
    result.error_message ??
    result.ErrorMessage ??
    result.ResultMessage ??
    result.RESULT_MESSAGE ??
    result.resultMessage ??
    result.result_message ??
    result.message ??
    result.Message ??
    "";

  return {
    success,
    message: String(message || ""),
    data: result,
  };
};

const getErrorMessage = (error, fallbackMessage) => {
  const normalizedError = normalizeManageUserResponse(
    error?.response?.data ?? error?.response,
  );

  return (
    normalizedError.message ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
};

function UserManageModal({
  open,
  mode,
  company,
  user,
  onClose,
  onSuccess,
  onChangePassword,
}) {
  const { t } = useEnglishText();
  const { showSuccess, showError } = useSnackbar();

  const isEditMode = mode === "edit";
  const isDeleteMode = mode === "delete";
  const isInsertMode = !isEditMode && !isDeleteMode;

  const [submitting, setSubmitting] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  /*
   * از هر سه ساختار زیر پشتیبانی می‌کند:
   *
   * user
   * [user]
   * [{ users: [user] }]
   */
  const selectedUser = useMemo(() => {
    const rootUser = Array.isArray(user) ? user[0] : user;

    if (Array.isArray(rootUser?.users)) {
      return rootUser.users[0] ?? null;
    }

    return rootUser ?? null;
  }, [user]);

  useEffect(() => {
    if (!open) return;

    setSubmitting(false);
    setChangingPassword(false);
    setShowPassword(false);

    if ((isEditMode || isDeleteMode) && selectedUser) {
      setFormData({
        username: String(
          selectedUser.USERNAME ??
            selectedUser.username ??
            selectedUser.USER_NAME ??
            "",
        ),

        // هیچ پسوردی در حالت Edit داخل فرم نگهداری نمی‌شود.
        password: "",

        user_email: String(
          selectedUser.EMAIL_ADDRESS ??
            selectedUser.email_address ??
            selectedUser.USER_EMAIL ??
            selectedUser.user_email ??
            selectedUser.EMAIL ??
            selectedUser.email ??
            "",
        ),

        access_child_agents: toBoolean(
          selectedUser.ACCESS_CHILD_AGENTS ?? selectedUser.access_child_agents,
          false,
        ),

        is_active: toBoolean(
          selectedUser.IS_ACTIVE ??
            selectedUser.is_active ??
            selectedUser.ACTIVE ??
            selectedUser.Active ??
            selectedUser.active,
          true,
        ),
      });
    } else {
      setFormData(initialFormData);
    }
  }, [open, selectedUser, isEditMode, isDeleteMode]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previousFormData) => ({
      ...previousFormData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClose = (event, reason) => {
    if (submitting || changingPassword) return;

    onClose?.(event, reason);
  };

  const handleChangePassword = async () => {
    if (
      submitting ||
      changingPassword ||
      typeof onChangePassword !== "function"
    ) {
      return;
    }

    const changePasswordUser = {
      ...selectedUser,
      SYS_USER_ID: selectedUser?.SYS_USER_ID ?? selectedUser?.sys_user_id ?? "",

      USERNAME: formData.username,

      EMAIL_ADDRESS: formData.user_email,
      user_email: formData.user_email,
    };

    try {
      setChangingPassword(true);

      await onChangePassword(changePasswordUser);
    } catch (error) {
      console.error("Change password error:", error);

      showError(
        getErrorMessage(
          error,
          t(
            "users.messages.change_password_error",
            "Changing the password failed.",
          ),
        ),
      );
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (submitting) return;

    const username = formData.username.trim();
    const email = formData.user_email.trim();

    if (!username) {
      showError(t("users.messages.username_required", "Username is required."));
      return;
    }

    if (isInsertMode && !formData.password) {
      showError(t("users.messages.password_required", "Password is required."));
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        action: isEditMode ? "UPDATE" : "INSERT",

        company_id: String(company?.company_id ?? company?.COMPANY_ID ?? ""),

        sys_user_id: isEditMode
          ? String(selectedUser?.SYS_USER_ID ?? selectedUser?.sys_user_id ?? "")
          : "",

        username,
        user_email: email,

        access_child_agents: formData.access_child_agents ? "1" : "0",

        is_active: formData.is_active ? "1" : "0",

        /*
         * پسورد فقط هنگام ساخت کاربر ارسال می‌شود.
         * در UPDATE اصلاً فیلد password داخل payload قرار نمی‌گیرد.
         */
        ...(isInsertMode
          ? {
              password: formData.password,
            }
          : {}),
      };

      const response = await manageUser(payload);
      const result = normalizeManageUserResponse(response);

      if (!result.success) {
        showError(
          result.message ||
            t("common.messages.error_occurred", "An error occurred."),
        );
        return;
      }

      showSuccess(result.message || t("common.success", "Success"));

      await onSuccess?.(result.data);
      onClose?.();
    } catch (error) {
      console.error("Manage user error:", error);

      showError(
        getErrorMessage(
          error,
          t("common.messages.error_occurred", "An error occurred."),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (submitting) return;

    const sysUserId = String(
      selectedUser?.SYS_USER_ID ?? selectedUser?.sys_user_id ?? "",
    );

    if (!sysUserId) {
      showError(t("users.messages.user_id_required", "User ID was not found."));
      return;
    }

    setSubmitting(true);

    try {
      const response = await manageUser({
        action: "DELETE",

        company_id: String(company?.company_id ?? company?.COMPANY_ID ?? ""),

        sys_user_id: sysUserId,
      });

      const result = normalizeManageUserResponse(response);

      if (!result.success) {
        showError(
          result.message ||
            t("common.messages.error_occurred", "An error occurred."),
        );
        return;
      }

      showSuccess(result.message || t("common.success", "Success"));

      await onSuccess?.(result.data);
      onClose?.();
    } catch (error) {
      console.error("Delete user error:", error);

      showError(
        getErrorMessage(
          error,
          t("common.messages.error_occurred", "An error occurred."),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={submitting || changingPassword}
      PaperProps={{
        sx: {
          direction: "ltr",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        {isDeleteMode
          ? t("users.delete_user", "Delete User")
          : isEditMode
            ? t("users.edit_user", "Edit User")
            : t("users.add_user", "Add User")}
      </DialogTitle>

      <DialogContent dividers>
        {isDeleteMode ? (
          <Typography
            sx={{
              py: 2,
              textAlign: "left",
            }}
          >
            {t("users.delete_confirm_text", {
              username: formData.username,
              defaultValue: `Are you sure you want to delete user "${formData.username}"?`,
            })}
          </Typography>
        ) : (
          <Box component="form" id="user-form" onSubmit={handleSave}>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                name="username"
                label={t("users.username", "Username")}
                value={formData.username}
                onChange={handleChange}
                fullWidth
                required
                disabled={submitting || changingPassword}
                autoComplete="username"
                inputProps={{
                  dir: "ltr",
                }}
              />

              {/* پسورد فقط هنگام ساخت کاربر نمایش داده می‌شود */}
              {isInsertMode && (
                <TextField
                  name="password"
                  label={t("users.password", "Password")}
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={submitting}
                  autoComplete="new-password"
                  inputProps={{
                    dir: "ltr",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          type="button"
                          onClick={() =>
                            setShowPassword((previousValue) => !previousValue)
                          }
                          onMouseDown={(event) => event.preventDefault()}
                          edge="end"
                          disabled={submitting}
                          aria-label={
                            showPassword
                              ? t("users.hide_password", "Hide password")
                              : t("users.show_password", "Show password")
                          }
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <TextField
                name="user_email"
                label={t("users.email", "Email")}
                type="email"
                value={formData.user_email}
                onChange={handleChange}
                fullWidth
                required
                disabled={submitting || changingPassword}
                autoComplete="email"
                inputProps={{
                  dir: "ltr",
                }}
              />

              {/* در حالت Edit به‌جای فیلد پسورد این دکمه نمایش داده می‌شود */}
              {isEditMode && (
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  startIcon={<LockResetIcon />}
                  onClick={handleChangePassword}
                  disabled={
                    submitting ||
                    changingPassword ||
                    typeof onChangePassword !== "function"
                  }
                  sx={{
                    alignSelf: "flex-start",
                  }}
                >
                  {changingPassword
                    ? t("users.changing_password", "Changing password...")
                    : t("users.change_password", "Change Password")}
                </Button>
              )}

              <FormControlLabel
                sx={{
                  mx: 0,
                  width: "100%",
                  justifyContent: "space-between",
                  flexDirection: "row",
                }}
                control={
                  <Switch
                    checked={formData.access_child_agents}
                    onChange={handleChange}
                    name="access_child_agents"
                    disabled={submitting || changingPassword}
                  />
                }
                label={t("users.access_child_agents", "Access Child Agents")}
              />

              <FormControlLabel
                sx={{
                  mx: 0,
                  width: "100%",
                  justifyContent: "space-between",
                  flexDirection: "row",
                }}
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleChange}
                    name="is_active"
                    disabled={submitting || changingPassword}
                  />
                }
                label={t("users.active", "Active")}
              />
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "space-between",
          flexDirection: "row",
        }}
      >
        <Button
          type="button"
          onClick={handleClose}
          disabled={submitting || changingPassword}
        >
          {t("common.cancel", "Cancel")}
        </Button>

        {isDeleteMode ? (
          <Button
            type="button"
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={submitting}
          >
            {submitting
              ? t("common.deleting", "Deleting...")
              : t("common.delete", "Delete")}
          </Button>
        ) : (
          <Button
            type="submit"
            form="user-form"
            variant="contained"
            color="primary"
            disabled={submitting || changingPassword}
          >
            {submitting
              ? t("common.saving", "Saving...")
              : t("common.save", "Save")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UserManageModal;
